/**
 * Gelesen/ungelesen im neuen Postfach (Paul 29.10.2026).
 *
 * „Man muss erkennen können, welche E-Mails gelesen/ungelesen sind, und sie
 * wieder auf ungelesen setzen können — das muss im E-Mail-Client vom Kunden /
 * IMAP auch so ankommen.“
 *
 * 🔴 Getrennt vom Arbeitsstatus (`status`): der hält eine Mail unter „Ein
 * Klick“/„Braucht dich“. `POST /api/v1/emails/gelesen` setzt nur das Gelesen-
 * Feld und \Seen im Postfach.
 */
import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import { toast } from "@/components/portal/Toast"

type MitGelesen = { id: number; gelesen?: boolean }
type Zeile = { gelesen?: boolean; email_direction?: string | null }

/** Ungelesen = eingehend und im Postfach nicht \Seen. Unbekannt gilt als gelesen. */
export function istUngelesen(z: Zeile): boolean {
  return z.gelesen === false && z.email_direction !== "sent"
}

export function zeilenKlasse(basis: string, aktiv: boolean, z: Zeile): string {
  return [basis, aktiv && "aktiv", istUngelesen(z) && "ungelesen"].filter(Boolean).join(" ")
}

/** Antwort des Servers: wie viele umgestellt, wie viele im Postfach offen. */
export type GelesenAntwort = { ok: boolean; geaendert: number; imap_offen: number; fehler?: { grund?: string }[] }

export async function gelesenSenden(ids: number[], gelesen: boolean): Promise<GelesenAntwort> {
  const r = await fetch("/api/v1/emails/gelesen", {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, gelesen }),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(j.detail || j.error || `HTTP ${r.status}`)
  return j as GelesenAntwort
}

function zeilen(liste: unknown, ids: Set<number>, gelesen: boolean): unknown {
  if (!Array.isArray(liste)) return liste
  return liste.map((z: MitGelesen) => (z && ids.has(z.id) ? { ...z, gelesen } : z))
}

/** Sofort in allen geladenen Listen umschalten — der Server antwortet danach. */
export function gelesenImCache(qc: QueryClient, ids: number[], gelesen: boolean): void {
  const weg = new Set(ids)
  qc.setQueriesData({ queryKey: ["emails"] }, (alt: unknown) => {
    if (!alt || typeof alt !== "object") return alt
    const o = alt as Record<string, unknown>
    const neu: Record<string, unknown> = { ...o }
    for (const k of ["items", "klick", "hand", "kenntnis"]) if (k in o) neu[k] = zeilen(o[k], weg, gelesen)
    return neu
  })
  for (const id of ids) {
    qc.setQueryData(["email", String(id)], (alt: { email?: MitGelesen } | undefined) =>
      alt?.email ? { ...alt, email: { ...alt.email, gelesen } } : alt)
  }
}

/** `still`: ohne Erfolgsmeldung (automatisch beim Öffnen). Fehler meldet es immer. */
export function useGelesen() {
  const qc = useQueryClient()
  return async (ids: number[], gelesen: boolean, still = false) => {
    if (!ids.length) return
    gelesenImCache(qc, ids, gelesen)
    try {
      const a = await gelesenSenden(ids, gelesen)
      if (a.geaendert < ids.length) {
        toast(`${a.geaendert} von ${ids.length} Mails geändert — ${a.fehler?.[0]?.grund || "Rest fehlgeschlagen"}`, "error")
      } else if (a.imap_offen) {
        toast(`Im Portal ${gelesen ? "gelesen" : "ungelesen"}, aber ${a.imap_offen === 1 ? "1 Mail" : `${a.imap_offen} Mails`} nicht im Postfach gefunden`, "error")
      } else if (!still) {
        toast(`✓ ${ids.length === 1 ? "1 Mail" : `${ids.length} Mails`} ${gelesen ? "gelesen" : "ungelesen"}`)
      }
    } catch (e) {
      gelesenImCache(qc, ids, !gelesen)
      toast(`${gelesen ? "Gelesen" : "Ungelesen"} fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, "error")
    } finally {
      // Beim Öffnen nicht alles neu laden — der Cache stimmt schon.
      if (!still) qc.invalidateQueries({ queryKey: ["emails"] })
    }
  }
}
